import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, Search } from "@vibe/core";
import { Locked } from "@vibe/icons";
import { useWorkspaceAndBoards } from "../../hooks/api/useWorkspacesAndBoards";
import { useBoardGroups } from "../../hooks/api/useBoardGroups";
import Spinner from "../Spinner";
import ButtonComponent from "../Button";
import { useMergeCandidates } from "./useMergeCandidates";
import { INELIGIBLE_REASON_TEXT, isSameName, sortCandidates } from "./mergeItem";
import type { MergeTarget } from "./mergeItem.types";

type Option = { label: string; value: string };

/**
 * The Settings window is a fixed-size monday modal (31.063rem × 56.125rem, SettingsDrawer.tsx) and
 * cannot grow. Its content pane scrolls, so a menu rendered inside it would be clipped by the pane.
 * Lifting the menu out of the pane and capping its height keeps it inside the window.
 */
const MENU_PROPS = {
  insideOverflowContainer: true,
  menuPlacement: "auto" as const,
  maxMenuHeight: 220,
};

interface MergeItemPickerProps {
  token: string | null;
  currentItemId: string;
  currentBoardId: string;
  currentItemName: string;
  /** Boards the current item is already synced to; they cannot take a second copy. */
  groupBoardIds: string[];
  value: MergeTarget | null;
  onChange: (target: MergeTarget | null) => void;
}

/** Step 1 (spec §2): board, then group, then the item. */
const MergeItemPicker = ({
  token,
  currentItemId,
  currentBoardId,
  currentItemName,
  groupBoardIds,
  value,
  onChange,
}: MergeItemPickerProps) => {
  const { workspacesAndBoards, isLoading: boardsLoading } = useWorkspaceAndBoards();
  const { boardGroups, fetchGroupsForBoard, isLoadingGroups } = useBoardGroups();

  const [board, setBoard] = useState<Option | null>(null);
  const [group, setGroup] = useState<Option | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const hiddenBoardIds = useMemo(
    () => new Set([String(currentBoardId), ...groupBoardIds.map(String)]),
    [currentBoardId, groupBoardIds],
  );

  const boardOptions: Option[] = useMemo(
    () =>
      (workspacesAndBoards ?? []).flatMap((workspace: any) =>
        (workspace?.boards ?? [])
          .filter((candidate: any) => !hiddenBoardIds.has(String(candidate.id)))
          .map((candidate: any) => ({
            label: workspace?.name ? `${candidate.name} · ${workspace.name}` : candidate.name,
            value: String(candidate.id),
          })),
      ),
    [workspacesAndBoards, hiddenBoardIds],
  );

  const groupOptions: Option[] = useMemo(
    () =>
      board
        ? (boardGroups[board.value] ?? []).map((candidate: any) => ({
            label: candidate.title,
            value: String(candidate.id),
          }))
        : [],
    [board, boardGroups],
  );

  useEffect(() => {
    if (board) fetchGroupsForBoard(board.value);
  }, [board, fetchGroupsForBoard]);

  const { items, isLoading, error, hasMore, loadMore } = useMergeCandidates({
    token,
    currentItemId,
    currentBoardId,
    boardId: board?.value ?? null,
    groupId: group?.value ?? null,
    searchTerm,
  });

  const sorted = useMemo(() => sortCandidates(items, currentItemName), [items, currentItemName]);

  const boardName = (option: Option | null) => option?.label.split(" · ")[0] ?? "";

  const selectBoard = (option: Option | null) => {
    setBoard(option);
    setGroup(null);
    setSearchTerm("");
    onChange(null);
  };

  const selectGroup = (option: Option | null) => {
    setGroup(option);
    setSearchTerm("");
    onChange(null);
  };

  const hiddenCount = hiddenBoardIds.size;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium" id="merge-board-label">
            Board
          </span>
          <Dropdown
            aria-labelledby="merge-board-label"
            placeholder={boardsLoading ? "Loading boards…" : "Choose a board"}
            options={boardOptions}
            value={board}
            onChange={(option: Option | null) => selectBoard(option)}
            searchable
            {...MENU_PROPS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium" id="merge-group-label">
            Group
          </span>
          <Dropdown
            aria-labelledby="merge-group-label"
            placeholder={!board ? "Choose a board first" : isLoadingGroups ? "Loading groups…" : "Choose a group"}
            options={groupOptions}
            value={group}
            onChange={(option: Option | null) => selectGroup(option)}
            disabled={!board}
            searchable
            {...MENU_PROPS}
          />
        </div>
      </div>
      <p className="m-0 -mt-2 text-xs text-[var(--secondary-text-color)]">
        {hiddenCount > 1
          ? "This item's board and the boards it is already synced to are not listed."
          : "This item's own board is not listed."}
      </p>

      {board && group && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">Item</span>
          <Search
            placeholder="Search items in this group"
            value={searchTerm}
            onChange={(next: string) => setSearchTerm(next)}
            size="medium"
          />

          {error && <p className="m-0 text-sm text-[var(--negative-color)]">{error}</p>}

          {isLoading && sorted.length === 0 ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : sorted.length === 0 && !error ? (
            <p className="m-0 py-4 text-sm text-[var(--secondary-text-color)]">
              {searchTerm ? "No items match your search." : "This group has no items."}
            </p>
          ) : (
            <fieldset className="m-0 p-0 border-0">
              <legend className="sr-only">Item to merge with</legend>
              <ul className="list-none m-0 p-0 border border-solid border-[#D0D4E4] rounded-md overflow-hidden">
                {sorted.map((item) => {
                  const blocked = item.eligibility?.eligible === false;
                  const reason =
                    item.eligibility && item.eligibility.eligible === false
                      ? INELIGIBLE_REASON_TEXT[item.eligibility.reason]
                      : null;
                  const checked = value?.itemId === item.id;
                  const sameName = isSameName(item.name, currentItemName);
                  return (
                    <li
                      key={item.id}
                      className={`border-0 border-b border-solid border-[#E7E9EF] last:border-b-0 ${
                        checked ? "bg-[#E6F1FD]" : blocked ? "bg-[#F6F7FB]" : ""
                      }`}
                    >
                      <label
                        className={`flex items-center gap-3 px-3 py-3 ${blocked ? "cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <input
                          type="radio"
                          name="merge-item"
                          value={item.id}
                          checked={checked}
                          disabled={blocked}
                          onChange={() =>
                            onChange({
                              boardId: board.value,
                              boardName: boardName(board),
                              groupId: group.value,
                              groupName: group.label,
                              itemId: item.id,
                              itemName: item.name,
                            })
                          }
                        />
                        <span className="flex flex-col flex-1 min-w-0 gap-0.5">
                          <span
                            className={`text-sm truncate ${blocked ? "text-[var(--secondary-text-color)]" : ""} ${
                              checked ? "font-medium" : ""
                            }`}
                          >
                            {item.name}
                          </span>
                          {reason && (
                            <span className="text-xs text-[var(--secondary-text-color)]">{reason}</span>
                          )}
                        </span>
                        {sameName && !blocked && (
                          <span className="text-xs font-semibold text-[#0060B9] bg-white border border-solid border-[#A9CDF5] rounded-full px-2 py-0.5">
                            Same name
                          </span>
                        )}
                        {blocked && <Locked aria-hidden size={16} className="text-[var(--secondary-text-color)]" />}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </fieldset>
          )}

          {hasMore && (
            <div className="flex justify-center">
              <ButtonComponent
                text="Load more items"
                kind="tertiary"
                size="small"
                loading={isLoading}
                onClick={() => void loadMore()}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MergeItemPicker;
